// Marketing landing — Instacart-inspired layout, EN/FR/HT via react-i18next.
//
// Structure:
//   1. Sticky header (logo, nav, language switcher, sign in, get started)
//   2. Hero: big headline + recipient phone input + primary CTA + receipt card
//   3. Product grid — the four lines; money and tuition are "Coming soon"
//      until a licensed partner is signed
//   4. How it works — 4 steps, headlined by the escrow promise
//   5. Built for how money really moves — 4 guarantees + partner line
//   6. Merchant CTA (claim or register a store)
//   7. Get-the-app CTA (mobile PWA install pitch)
//   8. Footer
//
// Every visible string is t()-keyed. Translations live in src/locales/.

import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ShoppingBag, ArrowRight, Check, Image as ImageIcon, MapPin, Smartphone as PhoneIcon, Store, Loader2, Package,
} from 'lucide-react';
import { LanguageSwitcher } from '../components/shared/LanguageSwitcher';
import { CurrencySwitcher } from '../components/shared/CurrencySwitcher';
import { storefront, carePackages, type MerchantItem, type CarePackageSummary } from '../lib/api';
import { iconFor } from '../lib/icons';
import { formatHtgMinor, formatDisplayFromHtgMinor } from '../lib/money';

export function Landing() {
  return (
    <div className="min-h-screen bg-white text-[var(--color-ink)]">
      <SiteHeader />
      <Hero />
      <ProductGrid />
      <CarePackagesStrip />
      <StorefrontStrip />
      <HowItWorks />
      <TrustStrip />
      <MerchantCta />
      <GetTheApp />
      <SiteFooter />
    </div>
  );
}

// ─── Photo placeholder ───────────────────────────────────────

/**
 * Stands in for the design's <image-slot>. The business still owes real
 * photos (handoff README, "Still needed from the business" #1), so this keeps
 * the caption visible rather than quietly rendering a pretty gradient — an
 * empty slot that looks finished never gets filled.
 */
function PhotoSlot({ label, className = '' }: { label: string; className?: string }) {
  return (
    <div
      role="img"
      aria-label={label}
      className={`relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-brand-100 via-tint-3 to-brand-50 ${className}`}
    >
      <div className="flex flex-col items-center gap-2 px-6 text-center">
        <ImageIcon className="w-7 h-7 text-brand-300" aria-hidden="true" />
        <span className="text-[11.5px] font-semibold leading-snug text-brand-600/70">{label}</span>
      </div>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────

function SiteHeader() {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-30 bg-white/92 backdrop-blur border-b border-hairline">
      <div className="mx-auto max-w-[1200px] px-6 h-[66px] flex items-center gap-[22px]">
        <Link to="/" className="flex items-center gap-2.5 flex-none">
          <span className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[11px] bg-gradient-to-br from-brand-500 to-brand-600 text-white">
            <ShoppingBag className="w-[17px] h-[17px]" aria-hidden="true" />
          </span>
          <span className="text-[18px] font-extrabold tracking-[-0.4px] text-ink">VoyeLakay</span>
        </Link>

        <nav className="hidden md:flex flex-1 min-w-0 items-center gap-5 text-[14px] whitespace-nowrap">
          <a href="#how" className="text-secondary hover:text-ink">{t('nav.how')}</a>
          <Link to="/claim" className="font-bold text-success-fg hover:underline">{t('nav.merchants')}</Link>
        </nav>
        <div className="flex-1 md:hidden" />

        <div className="flex items-center gap-2 flex-none">
          <CurrencySwitcher />
          <LanguageSwitcher />
          <Link to="/login" className="hidden sm:inline text-[14px] font-bold text-body hover:text-ink px-2 py-2 whitespace-nowrap">
            {t('nav.signIn')}
          </Link>
          <Link
            to="/register?role=customer"
            className="inline-flex items-center rounded-xl bg-ink px-4 py-2.5 text-[14px] font-extrabold text-white hover:bg-body transition-colors whitespace-nowrap"
          >
            {t('nav.getStarted')}
          </Link>
        </div>
      </div>
    </header>
  );
}

// ─── Hero ────────────────────────────────────────────────────

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-brand-600 to-brand-700 text-white">
      {/* Haiti flag stripe: blue over red, as on the national flag. */}
      <div className="absolute inset-x-0 top-0 flex h-[5px]" aria-hidden="true">
        <div className="flex-1 bg-interactive" />
        <div className="flex-1 bg-haiti-red" />
      </div>
      {/* Soft glow, top-right. */}
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(127,160,242,0.3), rgba(127,160,242,0) 70%)' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1200px] px-6 pt-[72px] pb-20 flex flex-wrap items-center gap-12">
        <div className="flex-1 basis-[480px] min-w-0">
          <p className="text-[12.5px] font-extrabold tracking-[1.6px] text-brand-300">{t('hero.kicker')}</p>

          <h1 className="mt-3.5 text-[40px] md:text-[58px] font-extrabold leading-[1.02] tracking-[-1.8px] text-balance">
            {t('hero.title')}
          </h1>

          <p className="mt-[18px] max-w-[520px] text-[17px] md:text-[18px] leading-[1.55] text-brand-100">
            {t('hero.subtitleV2')}
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const raw = new FormData(e.currentTarget).get('recipient_phone')?.toString().trim() || '';
              const qs = new URLSearchParams({ role: 'customer' });
              // The +509 prefix is chrome, not input — send E.164 so the
              // signup page does not have to guess the country.
              if (raw) qs.set('phone', `+509${raw.replace(/[^0-9]/g, '')}`);
              window.location.assign(`/register?${qs.toString()}`);
            }}
            className="mt-[30px] flex max-w-[500px] flex-wrap gap-2.5"
          >
            <div className="flex flex-1 basis-[240px] items-center overflow-hidden rounded-2xl bg-white focus-within:ring-2 focus-within:ring-white/40">
              <span className="border-r border-border px-3 py-[15px] pl-4 font-mono text-[15px] text-ink" aria-hidden="true">
                +509
              </span>
              <input
                type="tel"
                name="recipient_phone"
                inputMode="numeric"
                aria-label={t('hero.phoneLabel')}
                placeholder={t('hero.phonePlaceholderV2')}
                className="min-w-0 flex-1 bg-transparent px-3 py-[15px] text-[15px] text-ink placeholder:text-muted focus:outline-none"
                autoComplete="tel-national"
              />
            </div>
            <button
              type="submit"
              className="flex-none rounded-2xl bg-white px-[22px] py-[15px] text-[15px] font-extrabold text-brand-600 hover:bg-brand-50 transition-colors"
            >
              {t('hero.startV2')}
            </button>
          </form>

          <ul className="mt-5 flex flex-wrap gap-x-[18px] gap-y-2.5 text-[13.5px] text-brand-100">
            {['badge1', 'badge2', 'badge3'].map((k) => (
              <li key={k} className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {t(`hero.${k}`)}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex-1 basis-[380px] min-h-[440px] w-full">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  const { t } = useTranslation();
  return (
    <>
      <div className="absolute inset-y-0 left-0 right-0 sm:right-[60px] bottom-10 overflow-hidden rounded-[28px] shadow-[0_30px_70px_rgba(0,0,0,0.35)]">
        <PhotoSlot label={t('hero.photoAlt')} className="h-full w-full" />
      </div>

      {/* The proof the page is really selling: an order someone already
          confirmed. Sample data per the handoff — REQ-3311 is the same order
          the Order Flow design follows. */}
      <div className="absolute bottom-0 right-0 w-[260px] rounded-[20px] bg-white p-4 text-body shadow-[0_24px_50px_rgba(11,18,32,0.3)]">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11.5px] text-secondary-2">REQ-3311</span>
          <span className="rounded-md bg-success-bg px-2 py-[3px] text-[10.5px] font-extrabold text-success-fg">
            {t('card.confirmed')}
          </span>
        </div>
        <p className="mt-2 text-[15px] font-extrabold text-ink">{t('card.title')}</p>
        <p className="text-[12.5px] text-secondary-2">{t('card.merchant')}</p>
        <p className="mt-2.5 inline-flex items-center gap-2 text-[12.5px] font-bold text-success-fg">
          <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {t('card.confirmedAt')}
        </p>
      </div>
    </>
  );
}

// ─── Care packages strip (curated one-click bundles) ────────

function CarePackagesStrip() {
  const q = useQuery({
    queryKey: ['storefront', 'packages'],
    queryFn:  carePackages.list,
    staleTime: 5 * 60 * 1000,
  });

  // Hide the whole section if there's nothing to show (e.g. API down).
  if (q.data && q.data.length === 0) return null;

  return (
    <section className="border-t border-[var(--color-hairline)] py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-600 font-semibold inline-flex items-center gap-1.5">
              <Package className="w-3 h-3" /> One-click care packages
            </p>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-[var(--color-ink)]">
              Send a ready-made box home
            </h2>
            <p className="mt-2 text-[14px] text-[var(--color-secondary-2)] max-w-xl">
              Hand-picked bundles for the moments that matter — pick one, choose who it's for, and a merchant fills it.
            </p>
          </div>
          <Link to="/packages" className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:underline shrink-0">
            See all packages <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {q.isLoading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-44 rounded-2xl border border-[var(--color-hairline)] bg-white animate-pulse" />
            ))}
          </div>
        )}

        {q.data && q.data.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {q.data.slice(0, 6).map((p) => <PackageStripCard key={p.id} pkg={p} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function PackageStripCard({ pkg }: { pkg: CarePackageSummary }) {
  const Icon = iconFor(pkg.system_icon);
  return (
    <Link
      to={`/packages/${pkg.slug}`}
      className="group bg-white border border-[var(--color-hairline)] rounded-2xl p-5 hover:border-[var(--color-border)] hover:shadow-md transition-all flex flex-col"
    >
      <div className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/50 text-brand-600 mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-[15px] font-semibold text-[var(--color-ink)]">{pkg.name}</h3>
      {pkg.subtitle && <p className="mt-1 text-[12.5px] text-[var(--color-secondary-2)] leading-relaxed line-clamp-2">{pkg.subtitle}</p>}
      {pkg.merchant && (
        <p className="mt-1.5 text-[11px] text-[var(--color-muted)] truncate">{pkg.merchant.business_name}{pkg.merchant.city ? ` · ${pkg.merchant.city.name}` : ''}</p>
      )}
      <div className="mt-auto pt-3 flex items-baseline justify-between">
        {pkg.approx_htg_minor != null ? (
          <span className="text-[13.5px] font-semibold text-[var(--color-ink)] font-mono">≈ {formatDisplayFromHtgMinor(pkg.approx_htg_minor)}</span>
        ) : (
          <span className="text-[12px] text-[var(--color-secondary-2)]">{pkg.items_count} items</span>
        )}
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 group-hover:gap-2 transition-all">
          View <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </Link>
  );
}

// ─── Storefront strip (real merchant inventory) ─────────────

function StorefrontStrip() {
  // Public endpoint — no auth needed.
  const itemsQ = useQuery({
    queryKey: ['storefront', 'items', 12],
    queryFn:  () => storefront.items({ limit: 12 }),
    staleTime: 5 * 60 * 1000,                 // marketing page; 5min cache is fine
  });

  return (
    <section className="bg-gradient-to-b from-white to-[var(--color-panel)] border-t border-[var(--color-hairline)] py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-brand-600 font-semibold inline-flex items-center gap-1.5">
              <Store className="w-3 h-3" /> In stock today
            </p>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-[var(--color-ink)]">
              Shop real items from Léogâne merchants
            </h2>
            <p className="mt-2 text-[14px] text-[var(--color-secondary-2)] max-w-xl">
              Pulled live from approved merchants' inventory. Pick what your family needs — they'll see the quote in USD before you pay.
            </p>
          </div>
          <Link
            to="/register?role=customer"
            className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:underline shrink-0"
          >
            Sign in to shop the full catalog <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {itemsQ.isLoading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="h-36 rounded-xl border border-[var(--color-hairline)] bg-white animate-pulse" />
            ))}
          </div>
        )}

        {itemsQ.error && (
          <div className="text-center py-10 text-[13px] text-[var(--color-secondary-2)] inline-flex items-center gap-2 justify-center">
            <Loader2 className="w-4 h-4" /> Couldn't load live inventory right now.
          </div>
        )}

        {itemsQ.data && itemsQ.data.length === 0 && (
          <div className="text-center py-10 text-[13px] text-[var(--color-secondary-2)]">
            No items live yet — merchants are still loading inventory.
          </div>
        )}

        {itemsQ.data && itemsQ.data.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {itemsQ.data.map((it) => <StoreItemCard key={it.id} item={it} />)}
          </div>
        )}

        <p className="mt-6 text-[11px] text-[var(--color-muted)] text-center">
          Prices set by individual merchants. USD shown at ≈ 132.4 HTG / USD; the rate locks at quote time.
        </p>
      </div>
    </section>
  );
}

function StoreItemCard({ item }: { item: MerchantItem }) {
  const Icon = iconFor(item.system_icon);
  return (
    <Link
      to="/register?role=customer"
      className="group bg-white rounded-xl border border-[var(--color-hairline)] p-3 hover:border-[var(--color-border)] hover:shadow-md transition-all flex flex-col"
    >
      <div className="aspect-square rounded-lg bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/40 flex items-center justify-center text-brand-600 mb-3">
        <Icon className="w-9 h-9" />
      </div>
      <p className="text-[12.5px] font-semibold text-[var(--color-ink)] leading-tight line-clamp-2">{item.name}</p>
      <p className="text-[10.5px] text-[var(--color-secondary-2)] mt-0.5">{item.unit}</p>
      <div className="mt-auto pt-2 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-[var(--color-ink)] font-mono">
          {formatHtgMinor(item.price_htg_minor)}
        </span>
        <span className="text-[10px] text-[var(--color-muted)] font-mono">≈ {formatDisplayFromHtgMinor(item.price_htg_minor)}</span>
      </div>
      {item.merchant?.business_name && (
        <p className="text-[10px] text-[var(--color-muted)] mt-1 truncate">{item.merchant.business_name}</p>
      )}
    </Link>
  );
}

// ─── Categories ──────────────────────────────────────────────

function ProductGrid() {
  const { t } = useTranslation();
  // The Landing v2 design ships Money as live. It is badged SOON here because
  // it is not: VOYE_MONEY_ENABLED is false, the /ria routes are not even
  // registered in production, and RiaPartnerClient is still a stub. A public
  // page must not promise a rail that does not exist — flip this back the
  // moment Ria is signed and the flag goes on.
  const products = [
    { key: 'groceries', soon: false },
    { key: 'airtime',   soon: false },
    { key: 'money',     soon: true  },
    { key: 'tuition',   soon: true  },
  ] as const;

  return (
    <section id="send" className="px-6 py-[88px]">
      <div className="mx-auto max-w-[1200px]">
        <p className="text-[12.5px] font-extrabold tracking-[1.6px] text-interactive">{t('send.kicker')}</p>
        <h2 className="mt-2.5 max-w-[640px] text-[34px] md:text-[40px] font-extrabold tracking-[-1.1px] text-ink text-balance">
          {t('send.title')}
        </h2>

        <div className="mt-9 grid gap-4 grid-cols-[repeat(auto-fit,minmax(250px,1fr))]">
          {products.map((p) => (
            <article key={p.key} className="overflow-hidden rounded-3xl border border-border bg-white">
              <PhotoSlot label={t(`send.${p.key}_photo`)} className="aspect-[4/3] w-full" />
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <h3 className="text-[19px] font-extrabold text-ink">{t(`send.${p.key}_title`)}</h3>
                  {p.soon && (
                    <span className="rounded-md border border-warning-border bg-warning-bg px-[7px] py-0.5 text-[10.5px] font-extrabold text-warning-fg">
                      {t('send.soon')}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-[14px] leading-[1.5] text-secondary">{t(`send.${p.key}_body`)}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── How it works ────────────────────────────────────────────

function HowItWorks() {
  const { t } = useTranslation();
  const steps = ['s1', 's2', 's3', 's4'] as const;

  return (
    <section id="how" className="bg-app-bg px-6 py-[88px]">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-start gap-12">
        <div className="flex-1 basis-[340px]">
          <p className="text-[12.5px] font-extrabold tracking-[1.6px] text-interactive">{t('promise.kicker')}</p>
          <h2 className="mt-2.5 text-[34px] md:text-[40px] font-extrabold tracking-[-1.1px] text-ink text-balance">
            {t('promise.title')}
          </h2>
          <p className="mt-3.5 text-[16px] leading-[1.6] text-secondary">{t('promise.body')}</p>
          <Link
            to="/register?role=customer"
            className="mt-[22px] inline-flex items-center gap-1.5 rounded-[14px] bg-ink px-[18px] py-[13px] text-[14px] font-extrabold text-white hover:bg-body transition-colors"
          >
            {t('promise.cta')} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>

        <ol className="flex flex-[1.3] basis-[420px] flex-col gap-3">
          {steps.map((k, i) => {
            // Step 04 is the escrow release — the page's whole argument — so
            // the design gives it a green border instead of the default.
            const last = i === steps.length - 1;
            return (
              <li
                key={k}
                className={`flex gap-4 rounded-[20px] border bg-white px-[22px] py-5 ${last ? 'border-success-border' : 'border-border'}`}
              >
                <span
                  className={`w-10 flex-none text-[28px] font-extrabold tabular-nums ${last ? 'text-success-border' : 'text-tint-2'}`}
                  aria-hidden="true"
                >
                  0{i + 1}
                </span>
                <div>
                  <h3 className="text-[17px] font-extrabold text-ink">{t(`promise.${k}_title`)}</h3>
                  <p className="mt-1 text-[14px] leading-[1.55] text-secondary">{t(`promise.${k}_body`)}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

// ─── Trust strip ─────────────────────────────────────────────

function TrustStrip() {
  const { t } = useTranslation();
  const points = ['f1', 'f2', 'f3', 'f4'] as const;

  return (
    <section id="trust" className="px-6 py-[88px]">
      <div className="mx-auto max-w-[1200px]">
        <p className="text-[12.5px] font-extrabold tracking-[1.6px] text-interactive">{t('built.kicker')}</p>
        <h2 className="mt-2.5 text-[34px] md:text-[40px] font-extrabold tracking-[-1.1px] text-ink">
          {t('built.title')}
        </h2>

        <div className="mt-8 grid gap-3.5 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
          {points.map((k) => (
            <div key={k} className="rounded-[20px] border border-border p-[22px]">
              <h3 className="text-[16px] font-extrabold text-ink">{t(`built.${k}_title`)}</h3>
              <p className="mt-1.5 text-[14px] leading-[1.55] text-secondary">{t(`built.${k}_body`)}</p>
            </div>
          ))}
        </div>

        {/* The design carries a PARTNER NAMES PENDING CONFIRMATION badge here.
            Partner naming has since been confirmed, so the badge is dropped and
            the partners are named in the copy above. */}
        <p className="mt-[22px] text-[13px] text-muted">{t('built.partners')}</p>
      </div>
    </section>
  );
}

// ─── Get the app ─────────────────────────────────────────────

function GetTheApp() {
  const { t } = useTranslation();
  return (
    <section className="bg-gradient-to-br from-brand-600 to-brand-700 text-white">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-20 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-brand-100/90 font-semibold">{t('app.kicker')}</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">{t('app.title')}</h2>
          <p className="mt-4 text-brand-50/90 text-[15px] max-w-lg">
            <Trans
              i18nKey="app.body"
              values={{ url: 'lakaycart.com' }}
              components={{ 1: <code className="bg-white/10 rounded px-1.5 py-0.5 text-[13px]" /> }}
            />
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <Link to="/recipient" className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-white text-brand-700 text-[14px] font-semibold hover:bg-[var(--color-panel)] transition-colors">
              <PhoneIcon className="w-4 h-4" /> {t('app.open')}
            </Link>
            <a href="#how" className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-white/10 border border-white/20 text-white text-[14px] font-semibold hover:bg-white/20 transition-colors">
              {t('app.seeHow')}
            </a>
          </div>
        </div>
        <div className="flex justify-center md:justify-end">
          <div className="relative w-[260px] h-[440px] rounded-[38px] bg-[var(--color-ink)] border-8 border-[var(--color-ink)] shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-white p-4 flex flex-col">
              <p className="text-[10px] uppercase tracking-wider text-[var(--color-muted)] font-semibold">VoyeLakay</p>
              <h3 className="mt-1 text-[18px] font-semibold text-[var(--color-ink)]">{t('app.phone.header')}</h3>
              <p className="mt-1 text-[12px] text-[var(--color-secondary)]">{t('app.phone.sub')}</p>
              <button className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg bg-brand-600 text-white text-[13px] font-semibold">
                {t('app.phone.new')}
              </button>
              <p className="mt-6 text-[10px] uppercase tracking-wider text-[var(--color-muted)] font-semibold">{t('app.phone.recent')}</p>
              <div className="mt-2 rounded-lg border border-[var(--color-hairline)] p-3 text-[11px]">
                <p className="font-semibold text-[var(--color-ink)]">{t('categories.groceries.title')} · REQ-1042</p>
                <p className="text-[var(--color-success-fg)] mt-0.5">{t('app.phone.delivered')}</p>
              </div>
              <div className="mt-2 rounded-lg border border-[var(--color-hairline)] p-3 text-[11px]">
                <p className="font-semibold text-[var(--color-ink)]">{t('categories.pharmacy.title')} · REQ-1039</p>
                <p className="text-[var(--color-secondary-2)] mt-0.5">{t('app.phone.awaiting')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Merchant CTA ────────────────────────────────────────────

function MerchantCta() {
  const { t } = useTranslation();
  return (
    <section className="px-6 pb-[88px]">
      <div className="mx-auto flex max-w-[1200px] flex-wrap overflow-hidden rounded-[28px] bg-ink text-white">
        <div className="flex-1 basis-[420px] px-10 py-11">
          <p className="text-[12.5px] font-extrabold tracking-[1.6px] text-success-border">{t('merchants.kicker')}</p>
          <h2 className="mt-2.5 text-[28px] md:text-[34px] font-extrabold tracking-[-0.9px] text-balance">
            {t('merchants.title')}
          </h2>
          <p className="mt-3 max-w-[460px] text-[15px] leading-[1.6] text-brand-200">{t('merchants.body')}</p>

          <div className="mt-[22px] flex flex-wrap gap-2.5">
            <Link
              to="/claim"
              className="rounded-[14px] bg-white px-[18px] py-[13px] text-[14px] font-extrabold text-ink hover:bg-brand-50 transition-colors"
            >
              {t('merchants.claim')}
            </Link>
            <Link
              to="/register?role=merchant"
              className="rounded-[14px] border border-[#28304A] px-[18px] py-[13px] text-[14px] font-extrabold text-white hover:bg-white/10 transition-colors"
            >
              {t('merchants.register')}
            </Link>
          </div>
        </div>

        <div className="relative min-h-[300px] flex-1 basis-[360px]">
          <PhotoSlot label={t('merchants.photo')} className="absolute inset-0 h-full w-full" />
        </div>
      </div>
    </section>
  );
}

// ─── Footer ──────────────────────────────────────────────────

function SiteFooter() {
  const { t } = useTranslation();
  return (
    <footer className="bg-white border-t border-[var(--color-hairline)]">
      <div className="mx-auto max-w-7xl px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white">
              <ShoppingBag className="w-4 h-4" />
            </span>
            <span className="font-semibold tracking-tight text-[15px]">VoyeLakay</span>
          </div>
          <p className="mt-3 text-[13px] text-[var(--color-secondary-2)] leading-relaxed max-w-sm">{t('footer.tagline')}</p>
          <p className="mt-4 text-[12px] text-[var(--color-muted)] inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> {t('footer.city')}
          </p>
          <div className="mt-5"><LanguageSwitcher variant="labeled" /></div>
        </div>
        <FooterCol title={t('footer.product')} links={[[t('nav.categories'), '#categories'], [t('nav.how'), '#how'], [t('nav.trust'), '#trust']]} />
        <FooterCol title={t('footer.for')} links={[[t('footer.diaspora'), '/register?role=customer'], [t('footer.recipients'), '/recipient'], [t('footer.merchants'), '/register?role=merchant']]} />
        <FooterCol title={t('footer.company')} links={[[t('nav.signIn'), '/login'], [t('footer.contact'), 'mailto:hello@lakaycart.com'], [t('footer.status'), '/']]} />
      </div>
      <div className="border-t border-[var(--color-hairline)]">
        <div className="mx-auto max-w-7xl px-6 py-5 flex flex-wrap items-center justify-between gap-3 text-[12px] text-[var(--color-secondary-2)]">
          <p>{t('footer.copyright')}</p>
          <p>
            <Link to="/privacy" className="hover:text-[var(--color-ink)]">{t('footer.privacy')}</Link>
            <span className="mx-2 text-[var(--color-muted)]">·</span>
            <Link to="/terms" className="hover:text-[var(--color-ink)]">{t('footer.terms')}</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-[var(--color-muted)] font-semibold">{title}</p>
      <ul className="mt-3 space-y-2 text-[13px] text-[var(--color-secondary)]">
        {links.map(([label, href]) => (
          <li key={label}>
            {href.startsWith('/') ? (
              <Link to={href} className="hover:text-[var(--color-ink)]">{label}</Link>
            ) : (
              <a href={href} className="hover:text-[var(--color-ink)]">{label}</a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
